import { ConfigurationFieldType, EntityListResponse, EntityListTypeName } from '@ydistri/api-sdk';
import { EntityListConfiguration } from '@ydistri/utils';
import {
  BaseRootConfiguration,
  CategoryConfigurationData,
} from '../../../../../hooks/useCategoryConfiguration';
import { iterableSkuTypes } from '../../../../../helpers/types/iterableEnums';
import { CalculationConfigurationResponseTransformed } from '../../../../../apis/apiGlobalConfiguration';

export type UsableEntityListPredicate = (entityList: EntityListResponse) => boolean;

const defaultUsableEntityListPredicate: UsableEntityListPredicate = (
  entityList: EntityListResponse,
) => !entityList.isArchived && !entityList.isDataDeleted && entityList.entityListImport === null;

export const parseEntityListConfigs = (input: string): EntityListConfiguration[] =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we trust the input
  JSON.parse(input) as EntityListConfiguration[];

export const isEntityListTarget = (fieldType: ConfigurationFieldType): boolean =>
  fieldType === ConfigurationFieldType.PtMinLayerList;

export const createEntityListConfig = (
  entityListId: number,
  isEnabled: number,
): EntityListConfiguration => ({
  // eslint-disable-next-line @typescript-eslint/naming-convention -- mandated by API
  EntityListId: entityListId,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- mandated by API
  IsEnabled: isEnabled,
});

export const entityListTypeForConfiguration: Partial<
  Record<ConfigurationFieldType, EntityListTypeName[]>
> = {
  [ConfigurationFieldType.PsSkuScope]: [EntityListTypeName.Source],
  [ConfigurationFieldType.PsSkuScopeForced]: [EntityListTypeName.Forced],
  [ConfigurationFieldType.PtSkuScope]: [EntityListTypeName.Target],
  [ConfigurationFieldType.PsMinLayerList]: [EntityListTypeName.MinLayer],
  [ConfigurationFieldType.PtMinLayerList]: [EntityListTypeName.MinLayer],
  [ConfigurationFieldType.PtRequestList]: [EntityListTypeName.Request],
};

export const entityListScopeConfigurations: Partial<Record<ConfigurationFieldType, boolean>> = {
  [ConfigurationFieldType.PsSkuScope]: true,
  [ConfigurationFieldType.PsSkuScopeForced]: true,
  [ConfigurationFieldType.PtSkuScope]: true,
};

export const entityListScopeExcludedConfigurationPairs = {
  [ConfigurationFieldType.PsSkuScope]: ConfigurationFieldType.PsSkuScopeExclusion,
  [ConfigurationFieldType.PsSkuScopeForced]: ConfigurationFieldType.PsSkuScopeForcedExclusion,
  [ConfigurationFieldType.PtSkuScope]: ConfigurationFieldType.PtSkuScopeExclusion,
} as const;

export type ScopeConfigurationWithExcludedFieldType =
  keyof typeof entityListScopeExcludedConfigurationPairs;

export type ScopeConfigurationExclusionFieldType =
  (typeof entityListScopeExcludedConfigurationPairs)[keyof typeof entityListScopeExcludedConfigurationPairs];

export const isScopeConfigurationWithExcludedVariant = (
  value: ConfigurationFieldType,
): value is ScopeConfigurationWithExcludedFieldType => {
  return value in entityListScopeExcludedConfigurationPairs;
};
export const isScopeConfigurationExclusionVariant = (
  value: ConfigurationFieldType,
): value is ScopeConfigurationExclusionFieldType => {
  return !!Object.values(entityListScopeExcludedConfigurationPairs).find(c => c === value);
};

// Some scope configurations have related configurations that should be considered when working with them
const relatedEntityListFieldTypes: Partial<
  Record<ConfigurationFieldType, ConfigurationFieldType[]>
> = {
  [ConfigurationFieldType.PsSkuScope]: [ConfigurationFieldType.PsMinLayerList],
  [ConfigurationFieldType.PtSkuScope]: [
    ConfigurationFieldType.PtMinLayerList,
    ConfigurationFieldType.PtRequestList,
  ],
};

/**
 * Returns fields related to the given configuration field.
 * Some fields have related fields that should be considered when working with them.
 * @param configurationField The configuration field for which to get related fields.
 */
export const getRelatedEntityListFieldTypes = (
  configurationField: ConfigurationFieldType,
): ConfigurationFieldType[] => {
  return relatedEntityListFieldTypes[configurationField] ?? [];
};

/**
 * Returns only the entity lists that are directly related to the given configuration field.
 * It returns those entity lists that have been added as the main type but also through related fields.
 * @param configurationField configuration field for which to return configured entity lists
 * @param rootCategoryConfiguration configuration for root category
 * @param entityLists list of all entity lists
 * @param enabledOnly If true, only enabled entity lists will be returned.
 */
export const getConfiguredEntityLists = (
  configurationField: ConfigurationFieldType,
  rootCategoryConfiguration: BaseRootConfiguration,
  entityLists: EntityListResponse[],
  enabledOnly: boolean = false,
): EntityListResponse[] => {
  const config = rootCategoryConfiguration[configurationField];

  const usedEntityListObject: Record<number, boolean | undefined> = {};
  const usedEntityListIds: number[] = [];

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- i think it can be undefined
  if (config) {
    iterableSkuTypes.forEach(skuType => {
      const json = parseEntityListConfigs(config[skuType]);
      json.forEach(entityListConfig => {
        const savedObject = usedEntityListObject[entityListConfig.EntityListId];
        if (savedObject === undefined) {
          usedEntityListIds.push(entityListConfig.EntityListId);
        }
        //only set to true, never to false, so that if at least one SKU type has it enabled, it's enabled
        if (savedObject === false || savedObject === undefined) {
          usedEntityListObject[entityListConfig.EntityListId] = entityListConfig.IsEnabled === 1;
        }
      });
    });
  }

  return entityLists.filter(entityList => {
    return (
      usedEntityListIds.includes(entityList.entityListId) &&
      (enabledOnly ? usedEntityListObject[entityList.entityListId] : true)
    );
  });
};

/**
 * From the entity lists returns only those that are of type that corresponds to the given configuration field.
 * For example, for PsMinLayerList field only entity lists of type MinLayer will be returned.
 * @param configurationField The configuration field for which to return entity lists.
 * @param entityLists The list of all entity lists.
 * @param predicate Predicate to filter usable entity lists. By default, it filters out archived and lists being imported
 * @returns Entity lists that correspond to the given configuration field or undefined if no types are associated.
 */
export const getDirectEntityLists = (
  configurationField: ConfigurationFieldType,
  entityLists: EntityListResponse[],
  predicate: UsableEntityListPredicate = defaultUsableEntityListPredicate,
): EntityListResponse[] | undefined => {
  const entityListTypeNamesForField = entityListTypeForConfiguration[configurationField];
  if (entityListTypeNamesForField) {
    return entityLists.filter(predicate).filter(entityList => {
      const entityListTypes = entityList.entityListTypes.map(elt => elt.entityListTypeName);
      return entityListTypeNamesForField.some(fieldTypeName =>
        entityListTypes.includes(fieldTypeName),
      );
    });
  }
};

/**
 * Returns entity lists for related fields that are configured and enabled for at least one SKU type.
 * @param configurationField Field for which to get related fields
 * @param rootCategoryConfiguration Configuration for root category
 * @param entityLists All entity lists
 * @param calculationConfiguration Calculation configuration
 */
export const getAvailableRelatedEntityLists = (
  configurationField: ConfigurationFieldType,
  rootCategoryConfiguration: BaseRootConfiguration,
  entityLists: EntityListResponse[],
  calculationConfiguration: CalculationConfigurationResponseTransformed,
): EntityListResponse[] => {
  const result: EntityListResponse[] = [];

  const relatedEntityListFields = getRelatedEntityListFieldTypes(configurationField);

  relatedEntityListFields.forEach(relatedField => {
    if (relatedField === ConfigurationFieldType.PtRequestList) {
      if (calculationConfiguration.ptRequestListEntityList) {
        const requestListEntity = entityLists.find(
          el => el.entityListId === calculationConfiguration.ptRequestListEntityList?.entityListId,
        );
        if (requestListEntity) {
          result.push(requestListEntity);
        }
      }
    } else {
      const configuredEntityLists = getConfiguredEntityLists(
        relatedField,
        rootCategoryConfiguration,
        entityLists,
        true,
      );
      result.push(...configuredEntityLists);
    }
  });

  return result.filter(entityList => !entityList.isArchived && !entityList.isDataDeleted);
};

export interface ConfiguredEntityListVersion {
  /** The pinned version when every SKU type pins the same one. */
  entityListVersionId?: number;
  /** Whether the data of a version some SKU type pins were deleted. */
  isDataDeleted: boolean;
}

/**
 * The versions a versioned field pins for a list in a category, resolved per SKU type as its switches resolve them: the
 * nearest category's exception that names the list, else the root; undefined for a field that pins no version.
 * @param configurationField The configuration field
 * @param rootCategoryConfiguration Configuration for root category
 * @param categoryConfiguration The category's configuration with the exceptions of the category and its parents
 * @param entityListId The configured list
 */
export const getConfiguredEntityListVersion = (
  configurationField: ConfigurationFieldType,
  rootCategoryConfiguration: BaseRootConfiguration | undefined,
  categoryConfiguration: CategoryConfigurationData['categoryConfigurationData'],
  entityListId: number,
): ConfiguredEntityListVersion | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- a field missing in the configuration is undefined
  const root = rootCategoryConfiguration?.[configurationField];
  if (!root) return undefined;

  const pins = iterableSkuTypes
    .map(skuType => {
      const exceptions =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- a field missing in the configuration is undefined
        categoryConfiguration?.[configurationField]?.[skuType]?.exceptions ?? [];
      return (
        exceptions
          .map(e =>
            parseEntityListConfigs(e.value).find(item => item.EntityListId === entityListId),
          )
          .find(item => item !== undefined) ??
        parseEntityListConfigs(root[skuType]).find(item => item.EntityListId === entityListId)
      );
    })
    .filter(
      (item): item is EntityListConfiguration =>
        item !== undefined && (item.EntityListVersionId ?? 0) > 0,
    );
  if (pins.length === 0) return undefined;

  return {
    entityListVersionId:
      new Set(pins.map(item => item.EntityListVersionId)).size === 1
        ? pins[0].EntityListVersionId
        : undefined,
    isDataDeleted: pins.some(item => item.IsDataDeleted === 1),
  };
};

/**
 * Returns all entity lists that are available for a given configuration field. Availability means
 * that the entity list is either directly associated with the field or is linked via related fields and is enabled
 * for at least one SKU type and has not been configured yet.
 * @param configurationField field for which to get available entity lists
 * @param rootCategoryConfiguration configuration for root category
 * @param entityLists all entity lists
 * @param calculationConfiguration
 */
export const getAllAvailableEntityLists = (
  configurationField: ConfigurationFieldType,
  rootCategoryConfiguration: BaseRootConfiguration,
  entityLists: EntityListResponse[],
  calculationConfiguration: CalculationConfigurationResponseTransformed,
): EntityListResponse[] => {
  const directEntityLists = getDirectEntityLists(configurationField, entityLists) ?? [];

  const availableRelatedEntityLists = getAvailableRelatedEntityLists(
    configurationField,
    rootCategoryConfiguration,
    entityLists,
    calculationConfiguration,
  );

  const allPotentialEntityLists = [...directEntityLists, ...availableRelatedEntityLists];

  const configuredEntityLists = getConfiguredEntityLists(
    configurationField,
    rootCategoryConfiguration,
    entityLists,
  );

  const configuredEntityListIds = new Set(configuredEntityLists.map(el => el.entityListId));

  return allPotentialEntityLists.filter(el => !configuredEntityListIds.has(el.entityListId));
};
